<template>
  <div ref="searchMap" class="google-map" />
</template>

<script>
import { mapMutations } from 'vuex'
import { googleMapInit, defaultCenter } from '../../services/googleMapService'
import { isEmpty } from '../../utils/generalUtils'

export default {
  props: {
    searchResults: {
      type: Array,
      required: true
    },
    markedMarker: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      latLng: null,
      mapOptions: null,
      searchMap: null,
      markers: [],
      markerCluster: null
    }
  },

  watch: {
    searchResults: {
      handler: function(value) {
        this.populateMarkers(value)
        this.$emit('on-ready')
      },
      deep: true
    },

    markedMarker(value) {
      if (this.markers.length > 0) {
        if (value >= 0) {
          const selectedMarker = this.markers[value]
          this.searchMap.panTo(selectedMarker.getPosition())
          selectedMarker.setIcon('/img/gold-marker.png')
        } else {
          this.setMarkersToDefault()
        }
      }
    },

    markers: {
      handler: function(value) {
        if (value.length > 100) {
          // eslint-disable-next-line no-undef
          this.markerCluster = new MarkerClusterer(this.searchMap, value, {
            imagePath:
              'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
            maxZoom: 11
          })
        }
      }
    }
  },

  async mounted() {
    await this.loadGoogleService()
    window.addEventListener('keyup', event => this.escapeEvent(event))
  },

  beforeDestroy() {
    window.removeEventListener('keyup', event => this.escapeEvent(event))
  },

  methods: {
    ...mapMutations('common', {
      setMarkedId: 'SET_MARKED_ID'
    }),

    async loadGoogleService() {
      try {
        const google = await googleMapInit()
        // const geocoder = new google.maps.Geocoder()
        this.latLng = new google.maps.LatLng(
          defaultCenter.lat,
          defaultCenter.lng
        )

        this.mapOptions = {
          center: this.latLng,
          zoom: 11
        }

        this.searchMap = new google.maps.Map(
          this.$refs.searchMap,
          this.mapOptions
        )

        this.populateMarkers(this.searchResults)
        this.$emit('on-ready')
      } catch (error) {
        console.error(error)
      }
    },

    populateMarkers(results) {
      this.clearMarkers()
      if (!isEmpty(results)) {
        // set map center
        this.searchMap.setCenter({
          lat: results[0].Latitude,
          lng: results[0].Longitude
        })
        for (let i = 0; i < results.length; i++) {
          const result = results[i]
          const locationCoordinate = {
            position: {
              lat: result.Latitude,
              lng: result.Longitude
            }
          }

          const content = `<div id="iw-container">
          <div class="iw-content">
          <img src="${this.getImage(result)}" alt="Image">
          </div>        
          <div class="iw-title">
          <div class="iw-address">
            ${result.Address}, ${result.City}
          </div>
          <div class="text-xs-right">
            $${this.commaSeparated(result.ListPrice)}
          </div>
          </div>
          <div class="iw-table-container">
          <table class="iw-table">
          ${this.displayTable(result)}
          </table>
          </div>
          </div>`

          // eslint-disable-next-line no-undef
          google.maps.InfoWindow.prototype.isOpen = false
          // eslint-disable-next-line no-undef
          const infowindow = new google.maps.InfoWindow({ content })

          // eslint-disable-next-line no-undef
          const marker = new google.maps.Marker({
            ...locationCoordinate,
            map: this.searchMap,
            icon: '/img/blue-marker.png',
            infowindow
          // eslint-disable-next-line no-undef
          // animation: google.maps.Animation.DROP
          })

          this.searchMap.addListener('click', () => {
            this.closeInfoWindows()
            this.setMarkedId('')
          })

          marker.addListener('mouseover', () => {
            marker.setIcon('/img/gold-marker.png')
          })
          marker.addListener('click', () => {
            this.closeInfoWindows()
            infowindow.open(this.searchMap, marker)
            infowindow.isOpen = true
            marker.setIcon('/img/gold-marker.png')
            this.setMarkedId(result._id)
            this.$emit('marker-clicked', result)
          })
          marker.addListener('mouseout', () => {
            if (!infowindow.isOpen) {
              marker.setIcon('/img/blue-marker.png')
            }
          })
          this.markers.push(marker)
        }
      }
      return this.markers
    },

    getImage(result) {
      if (result.Media && result.Media.length > 0) {
        return result.Media[0].MediaURL
      }
      return '/img/gradient-image-loader.png'
    },

    displayTable(result) {
      let tableValue = ''
      const propertyParams = [
        { key: 'Type', value: result.PropertyType },
        {
          key: 'Size',
          value: `${this.commaSeparated(result.LivingArea)} SqFt`
        },
        {
          key: 'Rooms',
          value: `${result.BedroomsTotal} Beds + ${result.BathroomsFull} Baths`
        }
      ]

      if (propertyParams) {
        propertyParams.forEach(param => {
          tableValue += `<tr><td class="table-key">${param.key}</td><td class="table-value">${param.value}</td></tr>`
        })
        return tableValue
      }
      return null
    },

    clearMarkers() {
      for (let i = 0; i < this.markers.length; i++) {
        this.markers[i].setMap(null)
      }
      this.markers = []
      if (!isEmpty(this.markerCluster)) this.markerCluster.clearMarkers()
    },

    setMarkersToDefault() {
      for (let i = 0; i < this.markers.length; i++) {
        this.markers[i].setIcon('/img/blue-marker.png')
      }
    },

    closeInfoWindows() {
      for (let i = 0; i < this.markers.length; i++) {
        const marker = this.markers[i]
        marker.infowindow.close()
        marker.infowindow.isOpen = false
        marker.setIcon('/img/blue-marker.png')
      }
    },

    toggleBounce(marker) {
      if (marker.getAnimation() !== null) {
        marker.setAnimation(null)
      } else {
        // eslint-disable-next-line no-undef
        marker.setAnimation(google.maps.Animation.BOUNCE)
      }
    },

    commaSeparated(val) {
      return (val || '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    escapeEvent(event) {
      if (event.keyCode === 27) {
        this.closeInfoWindows()
        this.setMarkedId('')
      }
    }
  }
}
</script>

<style lang="sass">
// Styling Google Info Window
.gm-style .gm-style-iw-d
  overflow: hidden !important

.gm-style-iw
  padding: 0 !important
  border-radius: 0 !important
  text-align: center
  button
    display: none !important

#iw-container
  width: 25rem
  border: 0.4rem solid $hwayzYellow
  .iw-title
    font-size: 1.2rem
    color: $hwayzWhite
    padding: 1rem 0.5rem
    background-color: $hwayzBlack
    font-weight: 300
    position: relative
    .iw-address
      text-align: left
      margin-bottom: 0.5rem

#iw-container
  .iw-content
    font-size: 1.2rem
    line-height: 1.6rem
    height: auto
    min-height: 15rem
    position: relative
    img
      width: 100%
      height: auto

.iw-table-container
  padding: 1rem

.iw-table
  width: 100%
  padding: 1rem
  td
    padding: 0.3rem
  .table-key
    color: $hwayzBlack
    text-align: left
  .table-value
    color: $black
    text-align: right

// .iw-btn-assign
//   width: 80%
//   height: 2.5rem
//   line-height: 2.5rem
//   text-align: center
//   margin: 0.5rem auto 1.5rem
//   font-size: 1.2rem
//   border-radius: 1.5rem
//   background: $hwayzYellow
//   cursor: pointer
</style>

<style lang="sass" scoped>
.google-map
  position: relative
  width: 100%
  height: 100%
</style>
